/** @format */
/**
 * ConsentBanner — the only surface where a user can GRANT consent.
 *
 * Why it must exist: utils/consent.js fails closed, so with no UI every visitor
 * is permanently "denied", tagGate never loads GTM-56MR8T9P, and GA4 (which is
 * configured inside that container) silently records nothing. No error, no log
 * — just zero analytics. This component is what unblocks that.
 *
 * Deliberate design decisions, each preventing a specific failure:
 *  - Renders only when hasDecision() is false, and hides itself immediately
 *    after a choice. Re-prompting someone who already answered is nagging, and
 *    nagging is a recognised dark pattern.
 *  - "Reject all" is the same element type, same container and the same CSS
 *    class as "Accept all", one click each. A reject that is harder than accept
 *    (buried under "manage", greyed out, extra click) invalidates consent under
 *    GDPR/EDPB guidance — the consent collected would be worthless and the
 *    tracking done on the back of it unlawful.
 *  - Escape does NOT dismiss. Dismissal records nothing, so the banner returns
 *    on every page — nagging again — while analytics stays dead. There is no
 *    "close" affordance for the same reason: the only exits are real decisions.
 *  - No overlay / no aria-modal: it is a banner, not a wall. Blocking the page
 *    to force a choice is itself coercive and breaks keyboard users who want to
 *    read the privacy policy before deciding.
 */
import React, { useEffect, useRef, useState } from "react";
import { hasDecision, setConsent } from "../../utils/consent";
import "./ConsentBanner.css";

const ConsentBanner = ({ onManage, region = null, privacyHref = "/privacy" }) => {
  // Read once on mount. Re-reading on every render would let an unrelated
  // re-render resurrect the banner mid-interaction.
  const [visible, setVisible] = useState(() => !hasDecision());
  const containerRef = useRef(null);
  const firstActionRef = useRef(null);

  useEffect(() => {
    // Focus must land INSIDE the banner or keyboard/screen-reader users never
    // discover it and can never consent — an accessibility failure that reads
    // to a regulator as "no consent mechanism".
    if (visible && firstActionRef.current) {
      firstActionRef.current.focus();
    }
  }, [visible]);

  if (!visible) return null;

  const decide = (analytics, marketing) => {
    setConsent({ analytics, marketing, region });
    setVisible(false);
  };

  // Escape is swallowed here so a stray keypress cannot leave the user in the
  // "no decision" state described in the file header.
  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  return (
    <div
      className="consent-banner"
      role="dialog"
      aria-label="Cookie and tracking consent"
      ref={containerRef}
      onKeyDown={onKeyDown}
    >
      <div className="consent-banner__body">
        <h2 className="consent-banner__title">Your privacy choices</h2>
        <p className="consent-banner__text">
          We use strictly necessary cookies to run this site. With your
          permission we also use analytics and advertising cookies that collect
          your device and browser details, the pages you view and the actions
          you take here. That data is shared with <strong>Google</strong>{" "}
          (Google Analytics and Google Tag Manager) and <strong>Meta</strong>{" "}
          (Facebook and Instagram advertising), who act as recipients of it. You
          can change or withdraw your choice at any time.{" "}
          <a
            className="consent-banner__link"
            href={privacyHref}
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy policy
          </a>
        </p>
      </div>

      {/* Accept and Reject are siblings with an identical class on purpose:
          the equal-prominence requirement is asserted structurally in the
          tests, so restyling one of them alone will fail CI. */}
      <div className="consent-banner__actions">
        <button
          type="button"
          className="consent-banner__btn"
          ref={firstActionRef}
          onClick={() => decide(true, true)}
        >
          Accept all
        </button>
        <button
          type="button"
          className="consent-banner__btn"
          onClick={() => decide(false, false)}
        >
          Reject all
        </button>
        <button
          type="button"
          className="consent-banner__btn consent-banner__btn--tertiary"
          onClick={() => {
            if (typeof onManage === "function") onManage();
          }}
        >
          Manage preferences
        </button>
      </div>
    </div>
  );
};

export default ConsentBanner;
